import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Notification System
 *
 * Beautiful, reusable notification/toast/modal system for:
 * - Success messages
 * - Error messages
 * - Warnings
 * - Confirmations
 * - API responses
 *
 * Replaces all alert() and window.confirm() calls
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
const ToastContainer = styled.div `
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  min-width: 350px;
  max-width: 500px;
  background: var(--bg-secondary);
  border: 2px solid ${props => {
    switch (props.$type) {
        case 'success': return '#22c55e';
        case 'error': return '#ef4444';
        case 'warning': return '#f59e0b';
        case 'info': return '#3b82f6';
    }
}};
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 20px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    right: 16px;
    left: 16px;
    min-width: auto;
  }
`;
const ToastHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const ToastTitle = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  color: ${props => {
    switch (props.$type) {
        case 'success': return '#22c55e';
        case 'error': return '#ef4444';
        case 'warning': return '#f59e0b';
        case 'info': return '#3b82f6';
    }
}};

  svg {
    font-size: 20px;
  }
`;
const ToastMessage = styled.div `
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin-left: 32px;
`;
const CloseButton = styled.button `
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: var(--text-primary);
  }
`;
const Toast = ({ type, title, message, duration = 4000, onClose }) => {
    const [isVisible, setIsVisible] = React.useState(true);
    React.useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => onClose?.(), 300);
            }, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);
    if (!isVisible)
        return null;
    const getIcon = () => {
        switch (type) {
            case 'success': return _jsx(FaCheckCircle, {});
            case 'error': return _jsx(FaExclamationCircle, {});
            case 'warning': return _jsx(FaExclamationTriangle, {});
            case 'info': return _jsx(FaInfoCircle, {});
        }
    };
    return (_jsxs(ToastContainer, { "$type": type, children: [_jsxs(ToastHeader, { children: [_jsxs(ToastTitle, { "$type": type, children: [getIcon(), title] }), _jsx(CloseButton, { onClick: () => {
                            setIsVisible(false);
                            setTimeout(() => onClose?.(), 300);
                        }, children: _jsx(FaTimes, {}) })] }), message && _jsx(ToastMessage, { children: message })] }));
};
// Confirmation Modal Components
const ModalOverlay = styled.div `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const ModalContainer = styled.div `
  background: var(--bg-secondary);
  border: 2px solid ${props => {
    switch (props.$type) {
        case 'success': return '#22c55e';
        case 'error': return '#ef4444';
        case 'warning': return '#f59e0b';
        case 'info': return '#3b82f6';
    }
}};
  border-radius: 12px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: scaleIn 0.3s ease-out;

  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;
const ModalIcon = styled.div `
  text-align: center;
  margin-bottom: 20px;

  svg {
    font-size: 48px;
    color: ${props => {
    switch (props.$type) {
        case 'success': return '#22c55e';
        case 'error': return '#ef4444';
        case 'warning': return '#f59e0b';
        case 'info': return '#3b82f6';
    }
}};
  }
`;
const ModalTitle = styled.h2 `
  color: var(--text-primary);
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  margin: 0 0 12px 0;
`;
const ModalMessage = styled.p `
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.6;
  text-align: center;
  margin: 0 0 24px 0;
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 12px;
  justify-content: center;
`;
const ModalButton = styled.button `
  padding: 12px 32px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => {
    if (props.$variant === 'danger') {
        return `
        background: #ef4444;
        color: #FFFFFF;
        &:hover { background: #dc2626; transform: translateY(-1px); }
      `;
    }
    if (props.$variant === 'secondary') {
        return `
        background: transparent;
        color: var(--text-primary);
        border: 1px solid var(--border-color);
        &:hover { background: var(--bg-tertiary); border-color: var(--border-color-hover); }
      `;
    }
    return `
      background: #22c55e;
      color: #000000;
      &:hover { background: #16a34a; transform: translateY(-1px); }
    `;
}}
`;
const ConfirmModal = ({ type, title, message, confirmText = 'Confirm', cancelText = 'Cancel', onConfirm, onCancel }) => {
    const getIcon = () => {
        switch (type) {
            case 'success': return _jsx(FaCheckCircle, {});
            case 'error': return _jsx(FaExclamationCircle, {});
            case 'warning': return _jsx(FaExclamationTriangle, {});
            case 'info': return _jsx(FaInfoCircle, {});
        }
    };
    return (_jsx(ModalOverlay, { onClick: onCancel, children: _jsxs(ModalContainer, { "$type": type, onClick: e => e.stopPropagation(), children: [_jsx(ModalIcon, { "$type": type, children: getIcon() }), _jsx(ModalTitle, { children: title }), message && _jsx(ModalMessage, { children: message }), _jsxs(ButtonGroup, { children: [_jsx(ModalButton, { "$variant": "secondary", onClick: onCancel, children: cancelText }), _jsx(ModalButton, { "$variant": type === 'error' || type === 'warning' ? 'danger' : 'primary', onClick: onConfirm, children: confirmText })] })] }) }));
};
// Notification Manager
class NotificationManager {
    constructor() {
        this.container = null;
        this.root = null;
    }
    getContainer() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.id = 'notification-root';
            document.body.appendChild(this.container);
            this.root = createRoot(this.container);
        }
        return { container: this.container, root: this.root };
    }
    /**
     * Show a toast notification
     */
    toast(type, title, message, duration) {
        const { root } = this.getContainer();
        const cleanup = () => {
            root.render(null);
        };
        root.render(_jsx(Toast, { type: type, title: title, message: message, duration: duration, onClose: cleanup }));
    }
    /**
     * Show success toast
     */
    success(title, message, duration) {
        this.toast('success', title, message, duration);
    }
    /**
     * Show error toast
     */
    error(title, message, duration) {
        this.toast('error', title, message, duration);
    }
    /**
     * Show warning toast
     */
    warning(title, message, duration) {
        this.toast('warning', title, message, duration);
    }
    /**
     * Show info toast
     */
    info(title, message, duration) {
        this.toast('info', title, message, duration);
    }
    /**
     * Show confirmation modal
     */
    confirm(title, message, options) {
        return new Promise((resolve) => {
            const { root } = this.getContainer();
            const cleanup = () => {
                root.render(null);
            };
            const handleConfirm = () => {
                cleanup();
                resolve(true);
            };
            const handleCancel = () => {
                cleanup();
                resolve(false);
            };
            root.render(_jsx(ConfirmModal, { type: options?.type || 'warning', title: title, message: message, confirmText: options?.confirmText, cancelText: options?.cancelText, onConfirm: handleConfirm, onCancel: handleCancel }));
        });
    }
    /**
     * Handle API response with automatic notification
     */
    handleApiResponse(response, options) {
        if (response.ok && response.data?.success) {
            this.success(options?.successTitle || 'Success', options?.successMessage || response.data?.message || 'Operation completed successfully');
            return true;
        }
        else {
            this.error(options?.errorTitle || 'Error', response.error || response.data?.error || 'Operation failed');
            return false;
        }
    }
}
// Export singleton instance
export const notify = new NotificationManager();
// Export as default for easier imports
export default notify;
